import { Package, MapPin, Clock, Shield, Smartphone, ArrowRight, CheckCircle } from "lucide-react";

export default function HubDash() {
  return (
    <div className="min-h-screen bg-slate-50">

      {/* Hero */}
      <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-6 py-20 text-center">
          <div className="inline-flex items-center gap-2 bg-orange-500/20 border border-orange-500/30 rounded-full px-4 py-2 mb-6">
            <Package className="w-4 h-4 text-orange-400" />
            <span className="text-sm font-semibold text-orange-300 tracking-wide">NEW SERVICE</span>
          </div>
          <h1 className="text-5xl font-bold mb-4 tracking-tight">Hub Dash</h1>
          <p className="text-xl text-slate-300 mb-3 font-light">Same-day local courier · Ayrshire</p>
          <p className="text-slate-400 max-w-xl mx-auto leading-relaxed mb-10">
            Need something collected and delivered? Our local hub drivers handle it.
            Screwfix pickups, key handoffs, parcel deliveries — anything you need, same day.
          </p>
          <div className="inline-flex items-center gap-3 bg-orange-500/10 border border-orange-500/40 rounded-2xl px-6 py-4">
            <Smartphone className="w-5 h-5 text-orange-400 shrink-0" />
            <span className="text-slate-300 text-sm">
              Hub Dash is available exclusively on the <strong className="text-white">Ayrshire Hub app</strong>
            </span>
          </div>
        </div>
      </div>

      {/* How it works */}
      <div className="max-w-4xl mx-auto px-6 py-16">
        <h2 className="text-2xl font-bold text-foreground text-center mb-10">How it works</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {[
            { icon: MapPin,       step: "1", title: "Set pickup",    desc: "Search or drop a pin on the map for your pickup location in Ayrshire" },
            { icon: MapPin,       step: "2", title: "Set drop-off",  desc: "Tell us where it needs to go — any address in Ayrshire" },
            { icon: Package,      step: "3", title: "Get a price",   desc: "Instant price based on distance — no hidden fees, you see exactly what you pay" },
            { icon: CheckCircle,  step: "4", title: "Driver dispatched", desc: "A local hub driver accepts the job and sets off. You're notified every step" },
          ].map(({ icon: Icon, step, title, desc }) => (
            <div key={step} className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 text-center">
              <div className="w-10 h-10 rounded-full bg-orange-100 text-orange-600 font-bold text-lg flex items-center justify-center mx-auto mb-4">
                {step}
              </div>
              <Icon className="w-6 h-6 text-orange-500 mx-auto mb-3" />
              <h3 className="font-semibold text-foreground mb-2">{title}</h3>
              <p className="text-sm text-slate-500 leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Use cases */}
      <div className="bg-white border-y border-slate-100">
        <div className="max-w-4xl mx-auto px-6 py-14">
          <h2 className="text-2xl font-bold text-foreground text-center mb-3">Perfect for</h2>
          <p className="text-slate-500 text-center mb-10">Anything that needs to move from A to B, same day</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {[
              "Screwfix & tool store pickups",
              "Key handoffs to family or tenants",
              "Parcel & document delivery",
              "Click & collect orders",
              "Grocery runs for someone elderly",
              "Flowers & gifts delivered",
              "Forgotten items from home",
              "Business courier needs",
              "Anything that fits in a car",
            ].map((item) => (
              <div key={item} className="flex items-start gap-3 bg-slate-50 rounded-xl p-4">
                <CheckCircle className="w-4 h-4 text-green-500 shrink-0 mt-0.5" />
                <span className="text-sm text-slate-700 font-medium">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Trust points */}
      <div className="max-w-4xl mx-auto px-6 py-14">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
            <Clock className="w-7 h-7 text-orange-500 mb-4" />
            <h3 className="font-semibold text-foreground mb-2">Same-day delivery</h3>
            <p className="text-sm text-slate-500 leading-relaxed">Our drivers are already on the road across Ayrshire. Most pickups happen within the hour.</p>
          </div>
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
            <Shield className="w-7 h-7 text-orange-500 mb-4" />
            <h3 className="font-semibold text-foreground mb-2">Local trusted drivers</h3>
            <p className="text-sm text-slate-500 leading-relaxed">Every driver is approved and vetted by Ayrshire Hub. These are people from your community.</p>
          </div>
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
            <Package className="w-7 h-7 text-orange-500 mb-4" />
            <h3 className="font-semibold text-foreground mb-2">Fair & transparent pricing</h3>
            <p className="text-sm text-slate-500 leading-relaxed">You see the exact price before you pay — calculated by distance, no surge pricing, no surprises.</p>
          </div>
        </div>
      </div>

      {/* App download CTA */}
      <div className="bg-gradient-to-br from-orange-500 to-orange-700 text-white">
        <div className="max-w-2xl mx-auto px-6 py-16 text-center">
          <Smartphone className="w-12 h-12 mx-auto mb-5 opacity-90" />
          <h2 className="text-3xl font-bold mb-3 tracking-tight">Available on the Ayrshire Hub app</h2>
          <p className="text-orange-100 mb-8 leading-relaxed">
            Hub Dash is built into the Ayrshire Hub app — the same app you use to order food,
            find local businesses, and discover events. Download it free and tap <strong>Hub Dash</strong> on the home screen.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://apps.apple.com/gb/app/ayrshire-hub/id6766868864"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-white text-orange-600 font-bold px-6 py-3 rounded-xl hover:bg-orange-50 transition-colors"
            >
              <ArrowRight className="w-4 h-4" />
              Download on the App Store
            </a>
            <a
              href="https://play.google.com/store/apps/details?id=uk.co.ayrshirehub.app"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-white text-orange-600 font-bold px-6 py-3 rounded-xl hover:bg-orange-50 transition-colors"
            >
              <ArrowRight className="w-4 h-4" />
              Get it on Google Play
            </a>
          </div>
          <p className="text-orange-200 text-sm mt-5">
            Already have the app? Open it and tap <strong className="text-white">Hub Dash</strong> on the home screen.
          </p>
        </div>
      </div>

    </div>
  );
}
